import React, { useEffect, useRef } from "react";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/20/solid";

import { InputNumber } from "primereact/inputnumber";

import SelectMenu from "../../controls/SelectMenu";
import Input from "../../controls/Input";
import ActionButton from "../../controls/ActionButton";
import { formatCurrency } from "../../../shared/utility";

function formatQty(value: unknown) {
  if (value == null || value === "") return "0";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("en-US", { maximumFractionDigits: 3 });
}

function taxRateLabels(taxes: any[] = []) {
  if (!taxes.length) return null;
  return taxes
    .map((tax) => {
      const match = tax.label?.match(/^([^(]+)/);
      return match ? match[1].trim() : tax.label;
    })
    .join(", ");
}

export default function PurchaseOrderLineItemRow({
  row,
  assemblyItemOptions,
  onAssemblySelect,
  updateLineItem,
  saveLineItem,
  saveStatus = "idle",
  onRemove,
}: any) {
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    },
    [],
  );

  const scheduleSave = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      void saveLineItem(row.temp_id);
    }, 450);
  };

  const flushSave = () => {
    if (!saveTimerRef.current) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
    void saveLineItem(row.temp_id);
  };

  const updateEditableField = (patch: any) => {
    updateLineItem(row.temp_id, patch);
    scheduleSave();
  };

  const handleCommitKeyDown = (event: any) => {
    if (event.key === "Enter") event.currentTarget.blur();
  };

  const commitOrderedQuantity = (value: number | null | undefined) => {
    updateEditableField({
      ordered_quantity: value == null ? "" : String(value),
    });
  };
  const assemblyValue =
    assemblyItemOptions.find((o: any) => o.value === row.assembly_item_id) ||
    null;
  const uomLabel = String(row.unit_of_measure?.label ?? "").trim();
  const uomSuffix = uomLabel ? ` ${uomLabel}` : "";
  const orderedQuantity =
    row.ordered_quantity == null || row.ordered_quantity === ""
      ? null
      : Number(row.ordered_quantity);
  const received = row.received_amount ?? 0;
  const ordered = row.ordered_quantity;
  const fulfillment =
    row.fulfillment_percent != null
      ? row.fulfillment_percent
      : Number(ordered) > 0
        ? Math.round((received / Number(ordered)) * 10000) / 100
        : 0;
  const taxLabels = taxRateLabels(row.taxes);

  return (
    <tr className="border-b border-[#E2E8F0]/70 align-top">
      <td className="min-w-[200px] px-3 py-3">
        <SelectMenu
          name={`item-${row.temp_id}`}
          options={assemblyItemOptions}
          value={assemblyValue}
          handleInputChange={(e: any) =>
            onAssemblySelect(row.temp_id, e.target.value)
          }
          placeholder="Select item…"
          menuPortalTarget={document.body}
        />
      </td>
      <td className="w-36 px-3 py-3">
        <InputNumber
          name={`qty-${row.temp_id}`}
          inputId={`qty-${row.temp_id}`}
          value={Number.isFinite(orderedQuantity) ? orderedQuantity : null}
          onChange={(event) => commitOrderedQuantity(event.value)}
          onValueChange={(event) => commitOrderedQuantity(event.value)}
          onBlur={flushSave}
          onKeyDown={handleCommitKeyDown}
          min={0}
          maxFractionDigits={3}
          useGrouping={false}
          suffix={uomSuffix}
          inputClassName="w-full rounded-xl border border-[#E2E8F0] bg-white px-3 py-2 text-sm tabular-nums text-[#0F172A] shadow-sm"
          className="w-full"
        />
      </td>
      <td className="w-28 px-3 py-3 text-right text-sm tabular-nums text-[#64748B]">
        {`${formatQty(received)}${uomSuffix}`}
      </td>
      <td className="w-24 px-3 py-3 text-right">
        <div className="text-sm font-medium tabular-nums text-[#0F172A]">
          {fulfillment}%
        </div>
        <div className="text-xs tabular-nums text-[#64748B]">
          {formatQty(received)} / {formatQty(ordered)}
        </div>
      </td>
      <td className="w-28 px-3 py-3">
        <Input
          name={`cost-${row.temp_id}`}
          type="number"
          min="0"
          step="any"
          value={row.unit_cost}
          onChange={(e: any) =>
            updateEditableField({ unit_cost: e.target.value })
          }
          onBlur={flushSave}
          onKeyDown={handleCommitKeyDown}
        />
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-right text-sm font-medium tabular-nums text-[#0F172A]">
        {row.subtotal != null ? formatCurrency(row.subtotal) : "—"}
      </td>
      <td className="min-w-[100px] px-3 py-3 text-right">
        <div className="text-sm font-medium tabular-nums text-[#0F172A]">
          {row.tax_amount != null ? formatCurrency(row.tax_amount) : "—"}
        </div>
        {taxLabels ? (
          <div className="mt-0.5 text-xs text-[#64748B]">{taxLabels}</div>
        ) : null}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-right text-sm font-medium tabular-nums text-[#0F172A]">
        {row.line_total != null ? formatCurrency(row.line_total) : "—"}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          <span
            className="inline-flex min-w-[58px] items-center justify-end gap-1 text-xs font-medium"
            aria-live="polite"
          >
            {saveStatus === "saving" ? (
              <>
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#CBD5E1] border-t-[#2563EB]" />
                <span className="text-[#64748B]">Saving</span>
              </>
            ) : null}
            {saveStatus === "saved" ? (
              <>
                <CheckCircleIcon className="h-4 w-4 text-[#16A34A]" />
                <span className="text-[#15803D]">Saved</span>
              </>
            ) : null}
            {saveStatus === "error" ? (
              <>
                <ExclamationCircleIcon className="h-4 w-4 text-[#DC2626]" />
                <span className="text-[#B91C1C]">Failed</span>
              </>
            ) : null}
          </span>
          <ActionButton
            color="red"
            className="min-w-0 px-3"
            disabled={row.line_locked || saveStatus === "saving"}
            onClick={() => onRemove(row.temp_id)}
          >
            Delete
          </ActionButton>
        </div>
      </td>
    </tr>
  );
}
